import React, { useMemo, useState } from 'react';
import { Activity, ArrowRight, BarChart3, CalendarClock, Clock3, Sparkles, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ClickableKpiCard, KpiDetailPanel } from './common/ClickableKpiCard';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from './common/States';

const EmployeeOverviewDashboard = ({ records, todayRecord, chartData, formatDateTime, onNavigateTab, performanceSummary }) => {
  const [cardDetail, setCardDetail] = useState(null);
  const weeklyHours = useMemo(
    () => chartData.reduce((sum, item) => sum + (item.hours || 0), 0),
    [chartData]
  );

  const activeDays = useMemo(
    () => chartData.filter((item) => (item.hours || 0) > 0).length,
    [chartData]
  );

  const averageHours = activeDays ? weeklyHours / activeDays : 0;

  const statusData = useMemo(() => {
    const counts = records.reduce((acc, record) => {
      const status = record.status || 'Not Marked';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [records]);

  const punctualityRate = useMemo(() => {
    if (!records.length) return 0;
    const onTimeCount = records.filter((record) => {
      const status = (record.status || '').toUpperCase();
      return status === 'PRESENT' || status === 'ON_TIME';
    }).length;
    return (onTimeCount / records.length) * 100;
  }, [records]);

  const attendanceStreak = useMemo(() => {
    if (!records.length) return 0;
    const normalized = [...records]
      .map((record) => ({
        dateKey: formatDateTime(record.check_in_time || record.date, 'yyyy-MM-dd', null),
        status: String(record.status || '').toUpperCase(),
      }))
      .filter((record) => record.dateKey)
      .sort((a, b) => new Date(b.dateKey) - new Date(a.dateKey));

    let streak = 0;
    for (const record of normalized) {
      if (record.status === 'PRESENT' || record.status === 'ON_TIME' || record.status === 'LATE') {
        streak += 1;
      } else {
        break;
      }
    }
    return streak;
  }, [formatDateTime, records]);

  const todayStatusLabel = todayRecord?.status || 'Not marked';
  const todayStatusTone = String(todayStatusLabel).toLowerCase().includes('late')
    ? 'text-amber-600'
    : String(todayStatusLabel).toLowerCase().includes('present') || String(todayStatusLabel).toLowerCase().includes('on_time')
      ? 'text-success'
      : 'text-foreground';

  const recentRecords = useMemo(() => {
    return [...records]
      .sort((a, b) => new Date(b.check_in_time || b.date || 0) - new Date(a.check_in_time || a.date || 0))
      .slice(0, 6);
  }, [records]);
  const recentRecordsTable = useTableControls(recentRecords, {
    initialSortKey: null,
    initialSortDirection: 'desc',
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-hr-h1 font-bold tracking-tight">Employee Dashboard</h1>
        <p className="text-muted-foreground">Your workday, attendance, and personal action center.</p>
      </div>

      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-card to-[hsl(var(--chart-2))]/10">
        <CardContent className="pt-6">
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1 text-hr-xs">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Personal command center
              </div>
              <div>
                <h3 className="text-2xl font-semibold tracking-tight text-foreground">Your workweek at a glance</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Punctuality is at <span className="font-semibold text-foreground">{punctualityRate.toFixed(1)}%</span> with {weeklyHours.toFixed(1)} total hours and a {attendanceStreak}-day attendance streak.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-border bg-card/85 p-4 shadow-sm">
                  <p className="text-xs font-medium text-muted-foreground">Today Status</p>
                  <p className={`mt-2 text-2xl font-semibold ${todayStatusTone}`}>{todayStatusLabel}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Check-in {formatDateTime(todayRecord?.check_in_time, 'hh:mm a', '--:--')}</p>
                </div>
                <div className="rounded-xl border border-border bg-card/85 p-4 shadow-sm">
                  <p className="text-xs font-medium text-muted-foreground">Hours This Week</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{weeklyHours.toFixed(1)}h</p>
                  <p className="mt-1 text-xs text-muted-foreground">Average {averageHours.toFixed(1)}h per active day</p>
                </div>
                <div className="rounded-xl border border-border bg-card/85 p-4 shadow-sm">
                  <p className="text-xs font-medium text-muted-foreground">Performance</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{performanceSummary?.overall_score ?? '--'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Overall KPI score</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onNavigateTab?.('attendance')}
                  className="hover-lift inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm"
                >
                  Open Attendance
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onNavigateTab?.('profile')}
                  className="hover-lift inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm"
                >
                  Update Profile
                </button>
                <button
                  type="button"
                  onClick={() => onNavigateTab?.('helpdesk')}
                  className="hover-lift inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm"
                >
                  Raise Helpdesk
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <div className="animate-slide-up rounded-xl border border-border bg-card/85 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Need Attention</p>
                  <Activity className="h-4 w-4 text-primary" />
                </div>
                <p className="mt-3 text-lg font-semibold text-foreground">
                  {todayRecord?.check_in_time ? 'Attendance already marked' : 'Today attendance pending'}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {todayRecord?.check_in_time ? 'Continue with leave, payroll, or profile tasks.' : 'Use face verification to check in or check out from your attendance menu.'}
                </p>
              </div>
              <div className="animate-slide-up rounded-xl border border-border bg-card/85 p-4 shadow-sm" style={{ animationDelay: '0.05s' }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Momentum</p>
                  <TrendingUp className="h-4 w-4 text-[hsl(var(--chart-2))]" />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="success">Punctuality {punctualityRate.toFixed(0)}%</Badge>
                  <Badge variant="info">Active Days {activeDays}</Badge>
                  <Badge variant="warning">Avg {averageHours.toFixed(1)}h/day</Badge>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">Recent work pattern derived from your last seven attendance entries.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <ClickableKpiCard
          title="Today Status"
          value={todayRecord?.status || 'Not marked'}
          hint={`Check-in: ${formatDateTime(todayRecord?.check_in_time, 'hh:mm a', '--:--')}`}
          icon={Activity}
          cardClassName="border-primary/20 bg-primary/5"
          onClick={() => setCardDetail({
          title: 'Today Status',
          value: todayRecord?.status || 'Not marked',
          description: `Check-in time: ${formatDateTime(todayRecord?.check_in_time, 'hh:mm a', '--:--')}`,
          actionTab: 'attendance',
        })}
        />

        <ClickableKpiCard
          title="Hours This Week"
          value={`${weeklyHours.toFixed(1)}h`}
          hint="Based on your last 7 attendance records"
          icon={Clock3}
          iconWrapClassName="p-2 bg-[hsl(var(--chart-2))]/10 rounded-lg"
          iconClassName="h-4 w-4 text-[hsl(var(--chart-2))]"
          cardClassName="border-[hsl(var(--chart-2))]/20 bg-[hsl(var(--chart-2))]/10"
          onClick={() => setCardDetail({
          title: 'Hours This Week',
          value: `${weeklyHours.toFixed(1)}h`,
          description: 'Computed from your last 7 attendance records.',
          actionTab: 'finance-home',
        })}
        />

        <ClickableKpiCard
          title="Days Logged"
          value={activeDays}
          hint="Working days captured this week"
          icon={CalendarClock}
          iconWrapClassName="p-2 bg-[hsl(var(--chart-3))]/10 rounded-lg"
          iconClassName="h-4 w-4 text-[hsl(var(--chart-3))]"
          cardClassName="border-[hsl(var(--chart-3))]/20 bg-[hsl(var(--chart-3))]/10"
          onClick={() => setCardDetail({
          title: 'Days Logged',
          value: activeDays,
          description: 'Working days captured in the current weekly window.',
          actionTab: 'leave-history',
        })}
        />

        <ClickableKpiCard
          title="Avg Hours / Day"
          value={`${averageHours.toFixed(1)}h`}
          hint="Across days with recorded attendance"
          icon={BarChart3}
          iconWrapClassName="p-2 bg-[hsl(var(--chart-4))]/10 rounded-lg"
          iconClassName="h-4 w-4 text-[hsl(var(--chart-4))]"
          cardClassName="border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/10"
          onClick={() => setCardDetail({
          title: 'Avg Hours / Day',
          value: `${averageHours.toFixed(1)}h`,
          description: 'Average across days where attendance was recorded.',
          actionTab: 'attendance',
        })}
        />
      </div>

      <KpiDetailPanel
        detail={cardDetail}
        onClose={() => setCardDetail(null)}
        onOpenModule={() => onNavigateTab?.(cardDetail?.actionTab)}
        className="border-primary/20 bg-primary/5"
      />

      {performanceSummary && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-hr-lg">Key Employee Performance Parameters</CardTitle>
              <CardDescription>Data-driven KPI view for current period</CardDescription>
            </div>
            <Badge variant={performanceSummary.overall_score >= 80 ? 'success' : performanceSummary.overall_score >= 60 ? 'warning' : 'danger'}>
              Overall {performanceSummary.overall_score}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              {(performanceSummary.key_parameters || []).map((param) => (
                <div key={param.key} className="rounded-md border bg-background p-3">
                  <p className="text-hr-xs text-muted-foreground">{param.label}</p>
                  <p className="text-hr-lg font-bold">{param.score ?? 0}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Action Board</CardTitle>
            <CardDescription>Shortcuts based on your role permissions</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {[
              {
                title: 'Attendance & Face Check',
                description: 'Mark check-in or check-out and review your recent attendance history.',
                tab: 'attendance',
              },
              {
                title: 'Leave & Requests',
                description: 'Apply for leave, WFH, outstation, and regularization from one place.',
                tab: 'leave-balance',
              },
              {
                title: 'Payslips & Finance',
                description: 'Open salary, payslips, and reimbursements available to you.',
                tab: 'finance-home',
              },
              {
                title: 'Profile & Benefits',
                description: 'Keep profile and benefits data updated for smoother HR processing.',
                tab: 'benefits-my',
              },
            ].map((item, index) => (
              <button
                key={item.title}
                type="button"
                onClick={() => onNavigateTab?.(item.tab)}
                className="card-interactive animate-slide-up rounded-xl border border-border bg-card p-4 text-left hover:border-primary/20 hover:bg-muted"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Performance Snapshot</CardTitle>
            <CardDescription>Current review period summary</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {(performanceSummary?.key_parameters || [
              { key: 'attendance', label: 'Attendance', score: punctualityRate.toFixed(0) },
              { key: 'work_hours', label: 'Work Hours', score: weeklyHours.toFixed(1) },
              { key: 'consistency', label: 'Consistency', score: attendanceStreak },
            ]).map((param, index) => (
              <div
                key={param.key}
                className="animate-slide-up rounded-xl border border-border bg-muted p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-card"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{param.label}</p>
                <p className="mt-3 text-2xl font-semibold text-foreground">{param.score ?? 0}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Weekly Hours Trend</CardTitle>
            <CardDescription>Recent working hours by day</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip cursor={tooltipCursor} contentStyle={tooltipContentStyle} />
                <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Hours" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Attendance Status Split</CardTitle>
            <CardDescription>Distribution across your recorded attendance statuses</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={4}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={entry.name} fill={chartColor(index)} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipContentStyle} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <EmptyState
                  compact
                  icon={BarChart3}
                  title="No attendance yet"
                  description="Your attendance status breakdown will appear here once records are available."
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Check-In Time Trend
            </CardTitle>
            <CardDescription>When you usually start your day (last 7 records)</CardDescription>
          </CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData.map((item, idx) => ({ ...item, checkInIndex: idx + 1 }))}
              >
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip contentStyle={tooltipContentStyle} />
                <Line type="monotone" dataKey="hours" stroke={chartColor(1)} strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Attendance Records</CardTitle>
              <CardDescription>Latest check-in/check-out entries</CardDescription>
            </div>
            <Badge variant="outline">{recentRecordsTable.totalRows} records</Badge>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-hr-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Check In</th>
                    <th className="text-left p-3">Check Out</th>
                    <th className="text-left p-3">Hours</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRecordsTable.data.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-4">
                        <EmptyState
                          compact
                          icon={Clock3}
                          title="No recent records"
                          description="Your latest check-in and check-out entries will show up here."
                        />
                      </td>
                    </tr>
                  ) : (
                    recentRecordsTable.data.map((record) => (
                      <tr key={record.id} className="border-b hover:bg-muted/40 cursor-pointer" onClick={() => onNavigateTab?.('attendance')}>
                        <td className="p-3">{formatDateTime(record.check_in_time || record.date, 'dd MMM yyyy', '--')}</td>
                        <td className="p-3">{formatDateTime(record.check_in_time, 'hh:mm a', '--:--')}</td>
                        <td className="p-3">{formatDateTime(record.check_out_time, 'hh:mm a', '--:--')}</td>
                        <td className="p-3">{record.total_hours ? `${record.total_hours.toFixed(1)}h` : '--'}</td>
                        <td className="p-3">
                          <Badge variant="outline">{record.status || 'Not Marked'}</Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 pt-0">
              <TableControls
                totalRows={recentRecordsTable.totalRows}
                page={recentRecordsTable.page}
                totalPages={recentRecordsTable.totalPages}
                pageSize={recentRecordsTable.pageSize}
                pageSizes={recentRecordsTable.pageSizes}
                onPageSizeChange={recentRecordsTable.onPageSizeChange}
                onPrevious={recentRecordsTable.goToPreviousPage}
                onNext={recentRecordsTable.goToNextPage}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeOverviewDashboard;
